import React, { useState, useEffect } from "react";
import { Modal, Box, IconButton } from "@mui/material";
import { CancelIcon } from "../../../../../assets/icon";
import { useAuthenticatedFileUrl } from "../../../../../hooks/useAuthenticatedFileUrl";
import { ImageDrawing } from "../image-drawing/image-drawing";
import { Toolbar } from "../image-drawing/toolbar";
import { MdOutlineDraw } from "react-icons/md";

interface FullScreenImageProps {
  open: boolean;
  onClose: () => void;
  src: string;
  alt?: string;
  enableDrawing?: boolean;
}

export const FullScreenImage: React.FC<FullScreenImageProps> = ({
  open,
  onClose,
  src,
  alt = "Full screen image",
  enableDrawing = true,
}) => {
  const { url: resolvedSrc } = useAuthenticatedFileUrl(open ? src : undefined);
  const [selectedToolId, setSelectedToolId] = useState<string | null>(null);
  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const [isToolbarOpen, setIsToolbarOpen] = useState(true);

  const [isClear, setIsClear] = useState(false);

  useEffect(() => {
    if (open) {
      setIsToolbarOpen(true);
      if (!enableDrawing) {
        setSelectedToolId(null);
      }
    }
  }, [open, enableDrawing]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        zIndex: 3000,
      }}
    >
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(0, 0, 0, 0.9)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
          {enableDrawing ? (
            <ImageDrawing
              src={resolvedSrc || src}
              alt={alt}
              text={null}
              selectedToolId={selectedToolId}
              selectedColor={selectedColor}
              setSelectedToolId={setSelectedToolId}
              setSelectedColor={setSelectedColor}
              isClear={isClear}
              setIsClear={setIsClear}
              sx={{
                position: "absolute",
                objectFit: "contain",
                transition: "opacity 0.3s ease",
                cursor: "pointer",
                overflow: "visible",
              }}
            />
          ) : (
            <Box
              component="img"
              src={resolvedSrc || src}
              alt={alt}
              sx={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
              }}
            />
          )}

          {enableDrawing && isToolbarOpen && (
            <Box
              sx={{
                position: "absolute",
                left: "50%",
                bottom: (theme) =>
                  `max(${theme.spacing(2.5)}, env(safe-area-inset-bottom, 0px))`,
                transform: "translateX(-50%)",
                zIndex: 35,
                pointerEvents: "auto",
                maxWidth: "calc(100% - 16px)",
              }}
            >
              <Toolbar
                selectedToolId={selectedToolId}
                selectedColor={selectedColor}
                setSelectedToolId={setSelectedToolId}
                setSelectedColor={setSelectedColor}
                onClear={() => setIsClear(true)}
                onHide={() => setIsToolbarOpen(false)}
              />
            </Box>
          )}

          {enableDrawing && !isToolbarOpen && (
            <IconButton
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setIsToolbarOpen(true);
              }}
              aria-label="Show drawing tools"
              sx={{
                position: "absolute",
                left: "50%",
                bottom: (theme) =>
                  `max(${theme.spacing(2.5)}, env(safe-area-inset-bottom, 0px))`,
                transform: "translateX(-50%)",
                zIndex: 35,
                width: "2rem",
                height: "2rem",
                padding: "5px",
                borderRadius: "0.25rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#fafafa",
                opacity: "0.85",
                color: "#1f2937",
                pointerEvents: "auto",
                "&:hover": {
                  opacity: 1,
                  backgroundColor: "#fbfbfb",
                },
              }}
            >
              <MdOutlineDraw />
            </IconButton>
          )}
        </Box>
        </Box>

        <IconButton
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          aria-label="Close"
          sx={{
            position: "absolute",
            top: 20,
            right: 20,
            color: "#fff",
            zIndex: 40,
            pointerEvents: "auto",
          }}
        >
          <CancelIcon />
        </IconButton>
      </Box>
    </Modal>
  );
};
